import { ElMessage } from 'element-plus';
import { ref, computed, Ref } from 'vue';

export interface SortConfig {
  [key: string]: {
    label: string;
    mobileLabel: string;
    type: 'string' | 'date' | 'number';
    defaultOrder?: 'asc' | 'desc';
  };
}

export interface SortableItem {
  [key: string]: any;
}

export function sortingUtil<T extends SortableItem>(
  data: Ref<T[]>,  // 外部传入完整数据
  sortConfig: SortConfig,
) {
  // 获取第一个字段作为默认排序字段
  const defaultField = Object.keys(sortConfig)[0] || '';
  const sortField = ref<string>(defaultField);
  const sortOrder = ref<'asc' | 'desc'>(sortConfig[defaultField]?.defaultOrder || 'asc');
  const currentPage = ref<number>(1);
  const pageSize = ref<number>(10);

  // 处理排序
  const handleSort = (field: string) => {
    if (sortField.value === field) {
      // 如果点击的是当前排序字段，切换排序方向
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      // 如果点击的是新字段，使用该字段的默认排序方向
      sortField.value = field;
      sortOrder.value = sortConfig[field as keyof typeof sortConfig]?.defaultOrder || 'asc';
    }
    
  };

  // 获取排序字段的标签
  const getSortLabel = (field: string, isMobile: boolean = false) => {
    const config = sortConfig[field as keyof typeof sortConfig];
    return config ? (isMobile ? config.mobileLabel : config.label) : field;
  };

  // 获取排序图标
  const getSortIcon = (field: string) => {
    if (sortField.value !== field) {
      return 'unfold_more'; // 未排序状态
    }
    return sortOrder.value === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  };

  // 创建比较函数
  const createComparator = (field: string, order: 'asc' | 'desc') => {
    const config = sortConfig[field as keyof typeof sortConfig];
    const fieldType = config?.type || 'string';
    
    return (a: T, b: T) => {
      let aValue: any = a[field as keyof T];
      let bValue: any = b[field as keyof T];

      // 处理不同类型的字段
      switch (fieldType) {
        case 'date':
          aValue = new Date(aValue || 0).getTime();
          bValue = new Date(bValue || 0).getTime();
          break;
        case 'number':
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
          break;
        case 'string':
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }
          break;
      }

      // 处理空值
      if (aValue === null || aValue === undefined) aValue = fieldType === 'number' ? 0 : '';
      if (bValue === null || bValue === undefined) bValue = fieldType === 'number' ? 0 : '';

      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;

      // 根据排序方向返回结果
      return order === 'asc' ? comparison : -comparison;
    };
  };

  // 排序后的数据
  const sortedData = computed(() => {
    // console.log('sortedData', data.value);
    if (!sortField.value) return data.value;
    
    const comparator = createComparator(sortField.value, sortOrder.value);
    return [...data.value].sort(comparator);
  });

  // 分页后的数据
  const paginatedData = computed(() => {
    const startIndex = (currentPage.value - 1) * pageSize.value;
    const endIndex = startIndex + pageSize.value;
    return sortedData.value.slice(startIndex, endIndex);
  });

  // 总记录数
  const totalRecords = computed(() => sortedData.value.length);

  // 总页数
  const totalPages = computed(() => Math.ceil(sortedData.value.length / pageSize.value));

  const handlePageChange = (page: number) => {
    currentPage.value = page;
  };

  const handleSizeChange = (size: number) => {
    pageSize.value = size;
  };

  const showMoreDataAlert = (hasMoreData: boolean) => {
    if (hasMoreData) {
      ElMessage({
        message: '検索結果が1000件を超えています。より詳細な検索条件を設定してください。',
        type: 'warning',
        duration: 5000,
        showClose: true
      });
    }
  };

  const resetSorting = () => {
    sortField.value = '';
    sortOrder.value = sortConfig[sortField.value as keyof typeof sortConfig]?.defaultOrder || 'desc';
    currentPage.value = 1;
  };

  return {
    // State
    sortField,
    sortOrder,
    currentPage,
    
    // Computed
    sortedData,
    paginatedData,
    totalRecords,
    totalPages,
    pageSize,
    
    // Methods
    handleSort,
    getSortLabel,
    getSortIcon,
    handlePageChange,
    handleSizeChange,
    resetSorting,
    showMoreDataAlert,
  };
}